import React, { FunctionComponent, useContext, useEffect } from 'react';
import { useRefWithCurrentValue } from 'record-it-components/hooks/useRefWithCurrentValue';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PageLoadingOverlayContext } from '../context/PageLoadingOverlayContext';
import { useAuth } from '../hooks/useAuth';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useEntityManager } from '../hooks/entity/useEntityManager';

/**
 * A component which displays the AppPageLoadingOverlay when the user is logged in but the currentUser is not available.
 * This happens when the first actualization is not done yet.
 */
export const AppActualizationOverlayController: FunctionComponent = React.memo(
  (props) => {
    const { children } = prepareProps({ props });

    const { entityManager } = useEntityManager();
    const { isLoggedIn } = useAuth();
    const currentUserRef = useRefWithCurrentValue(useCurrentUser().currentUser);

    const { startLoading, addLoadingMessage } = useContext(
      PageLoadingOverlayContext
    );

    useEffect(() => {
      if (!isLoggedIn || currentUserRef.current) {
        return;
      }

      const loadingDisposable = startLoading();
      addLoadingMessage('synchronizing');

      const hooksDisposable = entityManager.entityActualization.registerHooks({
        afterActualization: () => {
          hooksDisposable.dispose();
          loadingDisposable.dispose();
        }
      });

      return () => {
        hooksDisposable.dispose();
        loadingDisposable.dispose();
      };
    }, [
      entityManager,
      isLoggedIn,
      currentUserRef,
      startLoading,
      addLoadingMessage
    ]);

    return (
      // eslint-disable-next-line react/jsx-no-useless-fragment
      <>{children}</>
    );
  }
);
